import { useEffect, useState } from 'react';
import api, { formatApiError, fileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Award, Plus, Trash2, FileText, ExternalLink } from 'lucide-react';
import FileUploader from './FileUploader';

export default function AchievementsSection({ employeeId, readonly = false }) {
  const { user } = useAuth();
  const targetId = employeeId || user?.id;
  const isOwner = user?.id === targetId;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', type: 'achievement', issuer: '', issued_on: '', description: '', file_id: null,
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/achievements?employee_id=${targetId}`);
      setItems(data);
    } catch (e) {
      // Silent — RM may not have access; that's fine
    }
  };
  useEffect(() => { if (targetId) load(); }, [targetId]); // eslint-disable-line

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      await api.post('/achievements', form);
      toast.success(`${form.type === 'certification' ? 'Certification' : 'Achievement'} added`);
      setOpen(false);
      setForm({ title: '', type: 'achievement', issuer: '', issued_on: '', description: '', file_id: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await api.delete(`/achievements/${id}`);
      toast.success('Removed');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="achievements-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Recognition</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">Achievements &amp; Certifications</h2>
        </div>
        {!readonly && isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-achievement-button" size="sm" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] h-9">
                <Plus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="add-achievement-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Add achievement / certification</DialogTitle>
                <p className="text-sm text-slate-500">Visible to HR &amp; your reporting manager.</p>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Type *</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="achv-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="achievement">Achievement</SelectItem>
                      <SelectItem value="certification">Certification</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Title *</Label>
                  <Input data-testid="achv-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. AWS Solutions Architect" className="rounded-xl mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Issuer</Label>
                    <Input data-testid="achv-issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="e.g. Amazon" className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Issued on</Label>
                    <Input data-testid="achv-date" type="date" value={form.issued_on} onChange={(e) => setForm({ ...form, issued_on: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Description</Label>
                  <Textarea data-testid="achv-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl mt-1" />
                </div>
                <FileUploader label="Attachment (optional)" testId="achv-file" fileId={form.file_id} onUploaded={(id) => setForm({ ...form, file_id: id })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-achievement" onClick={submit} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm" data-testid="no-achievements">
          <Award className="w-10 h-10 mx-auto opacity-30 mb-2" />
          {isOwner ? 'Add your first achievement or certification to highlight your growth.' : 'No achievements added yet.'}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(a => (
            <div key={a.id} data-testid={`achievement-${a.id}`} className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 transition-colors">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                a.type === 'certification' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
              }`}>
                <Award className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900">{a.title}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{a.type}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {a.issuer && <span>{a.issuer}</span>}
                  {a.issuer && a.issued_on && <span> · </span>}
                  {a.issued_on && <span>{a.issued_on}</span>}
                </div>
                {a.description && <div className="text-sm text-slate-700 mt-1">{a.description}</div>}
                {a.file_id && (
                  <a href={fileUrl(a.file_id)} target="_blank" rel="noreferrer" data-testid={`achievement-${a.id}-file`}
                     className="inline-flex items-center gap-1 text-xs text-[#3D4A8E] mt-1.5 hover:underline">
                    <FileText className="w-3 h-3" /> View certificate <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {!readonly && (isOwner || ['admin', 'hr'].includes(user?.role)) && (
                <button onClick={() => remove(a.id)} data-testid={`delete-achievement-${a.id}`} className="text-slate-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
